import { FC, useState } from "react";

import { SideSheetHeader } from "@/blocks/SideSheetHeader";
import { WorkRequirementMemberCard } from "@/blocks/WorkRequirementMemberCard";
import { VnBox } from "@/components/VnBox";
import { VnSheet, VnSheetContent } from "@/components/VnSheet";
import { useTrackVolunteerOfframpClickMutation } from "@/data/mutations/workRequirementsMutations";
import {
  WorkRequirementMemberDetailType,
  WorkRequirementSlotType,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { useTranslator } from "@/hooks/useTranslate";
import { dateAsDayjs } from "@/utils/date";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { openLinkInExternalBrowser } from "@/utils/linking";

import { WorkRequirementProofSheet } from "./WorkRequirementProofSheet";

type SelectedSlotType = {
  householdMemberId: string;
  coverageMonth: string | null;
};

type WorkRequirementMembersNextStepsPropsType = {
  workRequirement: WorkRequirementType;
};

const getSlotTitle = ({
  slot,
  t,
}: {
  slot: WorkRequirementSlotType;
  t: ReturnType<typeof useTranslator>["t"];
}) =>
  slot.coverageMonth
    ? t("native:workRequirements.nextSteps.proofOfWork", {
        month: dateAsDayjs(slot.coverageMonth).format("MMM YYYY"),
      })
    : t("native:workRequirements.nextSteps.proofOfExemption");

export const WorkRequirementMembersNextSteps: FC<
  WorkRequirementMembersNextStepsPropsType
> = ({ workRequirement }) => {
  const { t } = useTranslator();
  const { mutate: trackVolunteerOfframpClick } =
    useTrackVolunteerOfframpClickMutation();

  // Only the slot's identity is kept so the sheet always renders the latest
  // documents from the query cache as uploads get linked.
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlotType | null>(
    null,
  );

  const selectedMember = workRequirement.members.find(
    member => member.householdMemberId === selectedSlot?.householdMemberId,
  );
  const slot = selectedMember?.slots.find(
    ({ coverageMonth }) => coverageMonth === selectedSlot?.coverageMonth,
  );

  const handleVolunteerOpportunitiesPress = (
    member: WorkRequirementMemberDetailType,
  ) => {
    trackVolunteerOfframpClick({
      workRequirementId: workRequirement.id,
      householdMemberId: member.householdMemberId,
    });
    void openLinkInExternalBrowser(
      workRequirement.rule.volunteerOpportunitiesUrl,
    );
  };

  return (
    <>
      <VnBox className="gap-4">
        {workRequirement.members.map(member => (
          <WorkRequirementMemberCard
            key={member.id}
            member={member}
            rule={workRequirement.rule}
            onSlotPress={({ coverageMonth }) =>
              setSelectedSlot({
                householdMemberId: member.householdMemberId,
                coverageMonth,
              })
            }
            onVolunteerOpportunitiesPress={() =>
              handleVolunteerOpportunitiesPress(member)
            }
          />
        ))}
      </VnBox>

      <VnSheet
        open={Boolean(slot)}
        shouldShowCloseButton={false}
        overlayClassName="z-0"
        onOpenChange={isOpen => {
          if (!isOpen) {
            setSelectedSlot(null);
          }
        }}>
        <VnSheetContent className="md:max-w-1/3">
          {selectedMember && slot && (
            <>
              <SideSheetHeader
                title={formatHouseholdMemberName(selectedMember)}
                subtitle={getSlotTitle({ slot, t })}
              />
              <WorkRequirementProofSheet
                workRequirementId={workRequirement.id}
                householdMemberId={selectedMember.householdMemberId}
                slot={slot}
              />
            </>
          )}
        </VnSheetContent>
      </VnSheet>
    </>
  );
};
